import React, { useEffect, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Typography,
  Empty,
  Avatar,
  Skeleton,
  Card,
  Table,
  Tabs,
  Tag,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
} from "antd";
import { UserOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientDetails } from "../../Redux/Slice/Doctor/Patients";
import { createVitals } from "../../Redux/Slice/Doctor/Vitals";
import { toast } from "react-toastify";
import { fetchAllLab } from "../../Redux/Slice/Doctor/lab";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const PatientModal = ({ visible, onClose, patient }) => {
  const dispatch = useDispatch();
  const [vitalModal, setVitalModal] = useState(false);
  const [labModal, setLabModal] = useState(false);
  const [radiologyModal, setRadiologyModal] = useState(false);

  // ✅ Always define form instances at top level
  const [vitalForm] = Form.useForm();
  // const [labForm] = Form.useForm();
  // const [radiologyForm] = Form.useForm();

  const { data, loading } = useSelector(
    (state) => state.patient.patientDetails
  );

  const {
    data: vitalData,
    loading: vitalLoading,
    error: vitalError,
  } = useSelector((state) => state.vital.vitals);

  const { data: labData } = useSelector((state) => state.lab.Labs);

  console.log("labData", labData);

  const selectedBranchId = localStorage.getItem("selectedBranchId");

  console.log("selectedBranchId", selectedBranchId);

  useEffect(() => {
    if (patient) {
      dispatch(fetchPatientDetails(patient.patientId));
    }
  }, [dispatch, patient]);

  useEffect(() => {
    dispatch(fetchAllLab());
  }, [dispatch]);

  useEffect(() => {
    if (vitalData?.status) {
      toast.success(vitalData.message || "Vitals saved successfully");
      dispatch(fetchPatientDetails(patient.patientId));
      setVitalModal(false);
    } else if (vitalError?.message) {
      toast.error(vitalError.message || "Failed to save vitals");
    }
  }, [vitalData, vitalError, dispatch, patient?.patientId]);

  if (!patient) return null;

  const details = data?.data?.[0] || {};
  const demographics = details.demographics || {};
  const vitals = details.vitals || [];
  const emrs = details.emrs || [];
  const prescriptions = details.prescriptions || [];
  const prescriptionPdfs = details.prescriptionPdfs || [];
  const labReports = details.labReports || [];
  const radReports = details.radReports || [];

  const renderVital = (item) => {
    if (!item) return "N/A";
    return (
      <span>
        {item.value}{" "}
        {item.criticality === "critical" ? (
          <Tag color="red">{item.criticality}</Tag>
        ) : (
          <Tag color="green">{item.criticality}</Tag>
        )}
      </span>
    );
  };

  /** ===== Table Columns ===== **/
  const vitalsColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    {
      title: "BP (mmHg)",
      render: (_, r) => (
        <>
          {renderVital(r.bpSystolic)}/{renderVital(r.bpDiastolic)}
        </>
      ),
    },
    { title: "Heart Rate", render: (_, r) => renderVital(r.heartRate) },
    { title: "Temp (°C)", render: (_, r) => renderVital(r.bodyTemperature) },
    { title: "Height (cm)", render: (_, r) => renderVital(r.height) },
    { title: "Weight (kg)", render: (_, r) => renderVital(r.weight) },
    { title: "BMI", render: (_, r) => renderVital(r.bmi) },
  ];

  const emrColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    { title: "Complaint", dataIndex: "chiefComplaint", key: "chiefComplaint" },
    { title: "Diagnosis", dataIndex: "diagnosis", key: "diagnosis" },
  ];

  const prescriptionColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    { title: "Medicine", dataIndex: "medicineName", key: "medicineName" },
    { title: "Dosage", dataIndex: "dosage", key: "dosage" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    { title: "Freq", dataIndex: "frequency", key: "frequency" },
    { title: "Food", dataIndex: "foodInstruction", key: "foodInstruction" },
  ];

  const prescriptionPdfColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    {
      title: "PDF",
      dataIndex: "pdfUrl",
      key: "pdfUrl",
      render: (url) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          View
        </a>
      ),
    },
  ];

  const labColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    { title: "Test", dataIndex: "testName", key: "testName" },
    {
      title: "Report",
      dataIndex: "fileName",
      key: "fileName",
      render: (fileName, r) =>
        r.reportUrl ? (
          <a href={r.reportUrl} target="_blank" rel="noopener noreferrer">
            {fileName}
          </a>
        ) : (
          fileName || "N/A"
        ),
    },
  ];

  const radColumns = [
    { title: "Date", dataIndex: "createdOn", key: "date" },
    { title: "Test", dataIndex: "testName", key: "testName" },
    {
      title: "Report",
      dataIndex: "fileName",
      key: "fileName",
      render: (fileName, r) => (
        <a href={r.reportUrl} target="_blank" rel="noopener noreferrer">
          {fileName}
        </a>
      ),
    },
  ];

  /** ===== Form Submit Handlers (Mock) ===== **/
  const handleVitalSubmit = (values) => {
    const payload = { ...values, patientId: patient.patientId };
    console.log("Vital Payload:", payload);

    dispatch(createVitals(payload)); // send to redux
    // setVitalModal(false); // close modal
  };

  const handleLabSubmit = (values) => {
    console.log("Lab Payload:", { ...values, patientId: patient.patientId });
    setLabModal(false);
  };

  const handleRadiologySubmit = (values) => {
    console.log("Radiology Payload:", {
      ...values,
      patientId: patient.patientId,
    });
    setRadiologyModal(false);
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        styles={{ body: { padding: "20px" } }}
        confirmLoading={vitalLoading}
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Patient Details
              </Title>
            </Col>
            <Col style={{ marginRight: 30 }}>
              <Button
                icon={<PlusOutlined />}
                style={{ marginRight: 8 }}
                onClick={() => setVitalModal(true)}
                type="primary"
              >
                Add Vital
              </Button>
              {/* <Button
                icon={<PlusOutlined />}
                style={{ marginRight: 8 }}
                onClick={() => setLabModal(true)}
                type="primary"
              >
                Order Lab
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setRadiologyModal(true)}
                type="primary"
              >
                Order Radiology
              </Button> */}
            </Col>
          </Row>
        }
      >
        <Row gutter={24}>
          {/* ===== Demographics ===== */}
          <Col span={8}>
            <Card
              style={{ borderRadius: "10px" }}
              styles={{ body: { padding: "16px" } }}
            >
              {loading ? (
                <Skeleton active paragraph={{ rows: 10 }} />
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <Avatar
                      size={50}
                      icon={<UserOutlined />}
                      style={{ marginRight: 12, backgroundColor: "#1890ff" }}
                    />
                    <Title level={4} style={{ margin: 0 }}>
                      {demographics.name || "N/A"}
                    </Title>
                  </div>
                  <p>
                    <Text strong>MRD No:</Text>{" "}
                    <Text type="secondary">{demographics.mrdNo}</Text>
                  </p>
                  <p>
                    <Text strong>Age:</Text>{" "}
                    <Text type="secondary">{demographics.age}</Text>
                  </p>
                  <p>
                    <Text strong>Gender:</Text>{" "}
                    <Text type="secondary">{demographics.gender}</Text>
                  </p>
                  <p>
                    <Text strong>DOB:</Text>{" "}
                    <Text type="secondary">{demographics.dob}</Text>
                  </p>
                  <p>
                    <Text strong>Email:</Text>{" "}
                    <Text type="secondary">{demographics.emailId}</Text>
                  </p>
                  <p>
                    <Text strong>Phone:</Text>{" "}
                    <Text type="secondary">{demographics.phoneNo}</Text>
                  </p>
                  <p>
                    <Text strong>Address:</Text>{" "}
                    <Text type="secondary">{demographics.address}</Text>
                  </p>
                  <p>
                    <Text strong>Nationality:</Text>{" "}
                    <Text type="secondary">{demographics.nationality}</Text>
                  </p>
                  <p>
                    <Text strong>Visa Type:</Text>{" "}
                    <Text type="secondary">{demographics.visaType}</Text>
                  </p>
                  <p>
                    <Text strong>Occupation:</Text>{" "}
                    <Text type="secondary">{demographics.occupation}</Text>
                  </p>
                </>
              )}
            </Card>
          </Col>

          {/* ===== Medical Records Tabs ===== */}
          <Col span={16}>
            {loading ? (
              <Skeleton active paragraph={{ rows: 15 }} />
            ) : (
              <Tabs defaultActiveKey="1" type="line">
                <TabPane tab="Vitals" key="1">
                  {vitals.length > 0 ? (
                    <Table
                      dataSource={vitals}
                      columns={vitalsColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No Vitals" />
                  )}
                </TabPane>

                <TabPane tab="EMRs" key="2">
                  {emrs.length > 0 ? (
                    <Table
                      dataSource={emrs}
                      columns={emrColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No EMR Records" />
                  )}
                </TabPane>

                <TabPane tab="Prescriptions" key="3">
                  {prescriptions.length > 0 ? (
                    <Table
                      dataSource={prescriptions}
                      columns={prescriptionColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No Prescriptions" />
                  )}
                </TabPane>

                <TabPane tab="Prescription PDFs" key="4">
                  {prescriptionPdfs.length > 0 ? (
                    <Table
                      dataSource={prescriptionPdfs}
                      columns={prescriptionPdfColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No PDFs" />
                  )}
                </TabPane>

                <TabPane tab="Lab Reports" key="5">
                  {labReports.length > 0 ? (
                    <Table
                      dataSource={labReports}
                      columns={labColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No Lab Reports" />
                  )}
                </TabPane>

                <TabPane tab="Radiology Reports" key="6">
                  {radReports.length > 0 ? (
                    <Table
                      dataSource={radReports}
                      columns={radColumns}
                      rowKey="createdOn"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  ) : (
                    <Empty description="No Radiology Reports" />
                  )}
                </TabPane>
              </Tabs>
            )}
          </Col>
        </Row>
      </Modal>

      {/* ===== Add Vital Modal ===== */}
      <Modal
        open={vitalModal}
        onCancel={() => setVitalModal(false)}
        onOk={() => vitalForm.submit()}
        title="Add Vital"
        width={800}
      >
        <Form
          form={vitalForm}
          layout="vertical"
          onFinish={handleVitalSubmit}
          initialValues={{ patientId: patient.patientId }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="bpSystolic" label="BP Systolic">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bpDiastolic" label="BP Diastolic">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="heartRate" label="Heart Rate">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="height" label="Height (cm)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="weightMeasured" label="Weight (kg)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bodyMassIndex" label="BMI">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bodyTemperature" label="Temperature (°C)">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ===== Order Lab Modal ===== */}
      <Modal
        open={labModal}
        onCancel={() => setLabModal(false)}
        onOk={() => document.getElementById("labForm").submit()}
        title="Order Lab Test"
        width={800}
      >
        <Form
          id="labForm"
          layout="vertical"
          onFinish={handleLabSubmit}
          initialValues={{ patientId: patient.patientId }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="labTestId" label="Lab Test Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="branchId" label="Branch Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="doctorId" label="Doctor Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="Quantity">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="discountType" label="Discount Type">
                <Select>
                  <Option value="FLAT">FLAT</Option>
                  <Option value="PERCENTAGE">PERCENTAGE</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="discount" label="Discount">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ===== Order Radiology Modal ===== */}
      <Modal
        open={radiologyModal}
        onCancel={() => setRadiologyModal(false)}
        onOk={() => document.getElementById("radiologyForm").submit()}
        title="Order Radiology Test"
        width={800}
      >
        <Form
          id="radiologyForm"
          layout="vertical"
          onFinish={handleRadiologySubmit}
          initialValues={{ patientId: patient.patientId }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="labTestId" label="Test Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="branchId" label="Branch Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="doctorId" label="Doctor Id">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="Quantity">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label="Price">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="discount" label="Discount">
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remarks" label="Remarks">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="orderStatus" label="Order Status">
                <Select>
                  <Option value="Sent">Sent</Option>
                  <Option value="Pending">Pending</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default PatientModal;
